/* Writing suggested answers into a questionnaire item.
 *
 * One atom and a whole sweep share this module because they have to agree to
 * the character: a bulk accept that substituted differently from the single
 * accept beside it would write a care plan the clinician never reviewed. The
 * single accept folds ONE atom into the answer as stored; the sweep folds every
 * atom an item carries, in the order the record lists them, each onto the value
 * the previous one produced.
 *
 * The unit of a sweep is the QuestionnaireResponse, not the item and certainly
 * not the atom. Every item of a questionnaire lives in ONE resource, so a patch
 * per item would be several patches against the same record, each bumping its
 * version and each having to pass its concurrency test against what the last
 * one just wrote. `applyBulkAccept` instead runs the whole sweep as one pass
 * inside a single `mutateFn`: it applies completely or not at all, and takes
 * one version test.
 *
 * Inside that pass the sequencing still matters, because several atoms usually
 * target the same answer string — one scaffold line each. Every atom therefore
 * substitutes into the value the atom before it produced, read back off the
 * mutating instance rather than off the value the pass began with, exactly as
 * the single accept reads off the instance the patch will write. */

import type { Attachment, QuestionnaireItem } from '@medplum/fhirtypes'
import { questionnaireEntryOnlyFills } from '@seen/core/models/care-plans/fillOnly'
import {
  type PersistedQuestionnaireAnswerOp,
  questionnaireAnswerKey,
} from '@seen/core/models/compositionNote'
import {
  findLabelLine,
  substituteLabelValue,
  toInlineValue,
} from '#src/components/care-plan/answerLabelLines.ts'
import {
  ANSWER_CHANGED_REASON,
  type AnswerReversal,
  type AnswerValue,
} from '#src/components/care-plan/answerUndo.ts'
import { parseAnswerHtml } from '#src/components/care-plan/parseAnswerHtml.ts'

type SuggestedAnswer = PersistedQuestionnaireAnswerOp['answers'][number]

/* The one value an answer offers the item it targets, or undefined when it
 * offers none. The item's DECLARED TYPE picks the field, never which field
 * happens to be set: the tool schema marks both optional with no discriminated
 * union, so one answer may carry a string and a stray boolean at once, and
 * `valueBoolean ?? valueString` would hand a text item the boolean. `setAnswer`
 * parses by item type and THROWS on a mismatch — inside the caller's `mutateFn`,
 * so one mistyped answer would reject the whole patch and take every other
 * answer of the sweep down with it, permanently: the record is persisted, so
 * every retry fails identically.
 *
 * Types beyond boolean/string/text offer nothing either. Those items parse a
 * date, a number or a coding, none of which an answer carries, so the same
 * throw is the only thing passing one on could achieve. */
export function answerValueForItem(
  answer: SuggestedAnswer,
  item: QuestionnaireItem | undefined
): string | boolean | undefined {
  if (item?.type === 'boolean') {
    return typeof answer.valueBoolean === 'boolean'
      ? answer.valueBoolean
      : undefined
  }
  if (item && item.type !== 'string' && item.type !== 'text') {
    return undefined
  }
  const proposed = answer.valueString ?? ''
  return proposed.trim() === '' ? undefined : proposed
}

/* An answer the clinician could actually accept. `setAnswer` treats undefined,
 * empty and whitespace-only strings as "remove this answer", so an answer
 * carrying no usable value would DELETE existing text on accept — the
 * destructive case, since these fields normally already hold authored text.
 *
 * Callers that have not resolved the questionnaire yet pass no item and get the
 * presence test alone; every path that WRITES resolves one first, and gates
 * again on it. Type-gating without an item in hand would drop the boolean
 * answers a sweep is supposed to carry. */
export function hasValue(
  answer: SuggestedAnswer,
  item: QuestionnaireItem | undefined
): boolean {
  if (!item) {
    return (
      typeof answer.valueBoolean === 'boolean' ||
      (answer.valueString ?? '').trim() !== ''
    )
  }
  return answerValueForItem(answer, item) !== undefined
}

type StoredAnswerValue = string | number | boolean | Attachment | undefined

/* Whether writing `answer` where it lands would change what the item now
 * holds rather than fill it in: the line (or the whole item, for an unlabelled
 * atom) has been answered since the AI proposed it, and the value does not
 * keep that answer word for word. Staging checked the answer as it stood then;
 * a clinician may have filled the line in since.
 *
 * Asked only of atoms staged under the fill-only rule. A call made without the
 * rule was allowed to rewrite what was there, so an answer filled in since is
 * not a reason to hold its atom back. The gate is the first line below and
 * nowhere else, so applying this to every atom is a one-line change. */
export function questionnaireAtomTargetChanged(
  current: StoredAnswerValue,
  answer: SuggestedAnswer,
  questionnaireUrl: string
): boolean {
  if (answer.fillOnly !== true) {
    return false
  }
  return !questionnaireEntryOnlyFills(
    questionnaireUrl,
    answer,
    current ?? null,
    parseAnswerHtml
  )
}

/* Why an undecided atom has nowhere to go in the answer as it now stands:
 *
 *   - `targetChanged` — its line was answered since the AI proposed it, and
 *     writing the value would overwrite that (`questionnaireAtomTargetChanged`).
 *   - `lineMissing` — the answer no longer holds the labelled line (or the
 *     section) it was written for.
 *   - `lineTaken` — an earlier atom of the same item already resolves to that
 *     line, and two values cannot both be written through it. */
export type UnplaceableAtomReason =
  | 'targetChanged'
  | 'lineMissing'
  | 'lineTaken'

/* The atoms of ONE item that cannot be placed into `current`, keyed by
 * `questionnaireAnswerKey`. `atoms` are the item's undecided atoms in the order
 * the record lists them, which is the order lines are claimed in: the first
 * atom to resolve to a line owns it.
 *
 * An atom held back for any reason claims nothing, so a later atom for the same
 * line is judged on its own rather than reported as a duplicate of one that is
 * not going to be written. An unlabelled atom proposes the whole answer, so it
 * can only be held back by the fill-only check.
 *
 * The card surface, the single accept and the bulk accept all ask this, so an
 * atom the card does not show can never be written by either accept. */
export function unplaceableQuestionnaireAtoms(
  questionnaireUrl: string,
  current: StoredAnswerValue,
  atoms: readonly SuggestedAnswer[]
): Map<string, UnplaceableAtomReason> {
  const unplaceable = new Map<string, UnplaceableAtomReason>()
  const html = typeof current === 'string' ? current : ''
  const claimed = new Set<number>()
  for (const atom of atoms) {
    const key = questionnaireAnswerKey(atom)
    const line = atom.label
      ? findLabelLine(html, atom.label, atom.section ?? undefined)
      : null
    if (atom.label && line === null) {
      unplaceable.set(key, 'lineMissing')
      continue
    }
    if (questionnaireAtomTargetChanged(current, atom, questionnaireUrl)) {
      unplaceable.set(key, 'targetChanged')
      continue
    }
    if (line === null) {
      continue
    }
    if (claimed.has(line)) {
      unplaceable.set(key, 'lineTaken')
      continue
    }
    claimed.add(line)
  }
  return unplaceable
}

/* The value an accept writes into the item, given the answer as stored right
 * now. A labelled atom substitutes into that answer rather than replacing the
 * field: anything a sibling atom or the clinician wrote into the other lines
 * has to survive. `section` is what makes a repeated label addressable —
 * without it every `Goal:` atom of an item would rewrite the first one.
 *
 * A labelled value is flattened to inline html first: an island edit arrives as
 * the island document's own HTML, which is always block-wrapped, and a block
 * substituted into a line would land inside that line's paragraph. An unlabelled
 * atom replaces the whole answer, where blocks are what the answer is made of. */
export function acceptedAnswerValue(
  current: AnswerValue,
  answer: SuggestedAnswer,
  edited: string | undefined,
  item: QuestionnaireItem | undefined
): string | boolean | undefined {
  const value = answerValueForItem(answer, item)
  if (typeof value !== 'string') {
    return value
  }
  const proposed = edited ?? value
  if (!answer.label) {
    return proposed
  }
  /* Undefined when the answer holds no line for this label: the substitution
   * refuses to place a value the scaffold offers no line for, and this function
   * already spells "nothing to write" that way. */
  return (
    substituteLabelValue(
      typeof current === 'string' ? current : '',
      answer.label,
      toInlineValue(proposed),
      answer.section
    ) ?? undefined
  )
}

// One item's share of a bulk accept: the single value the sweep writes, and
// what it takes to put the item back.
export interface BulkAcceptItemWrite {
  linkId: string
  // The atoms this one write settled, so the batch's undo re-pends exactly
  // them and nothing the clinician had decided by hand.
  atomKeys: string[]
  /* The item's WHOLE answer either side of the write. The item is the unit of
   * reversal as well as of writing: the sweep rewrote several of its lines at
   * once, so restoring the answer it found is both precise and cheap. */
  before: AnswerValue
  after: string | boolean
}

/* What one sweep did to a whole QuestionnaireResponse: the per-item writes, and
 * the atoms it had to leave alone.
 *
 * The two are reported separately because they settle differently. A written
 * atom is decided and reversible; an unplaceable one is neither, and the record
 * it belongs to is not finished while any of them stands. Atoms dropped for
 * carrying no value or a value the item's type cannot take are NOT counted
 * here: they were never a decision the clinician could take, and the single
 * accept's record status leaves them out for the same reason. */
export interface BulkAcceptResult {
  writes: BulkAcceptItemWrite[]
  /* Atoms whose value the answer holds no line to write through, whose line
   * another atom already fills, or whose value would overwrite what the
   * fill-only rule keeps, so the sweep passed over them and they are still to
   * be dismissed from the couldn't-be-placed list. */
  unplaceableAtomKeys: string[]
}

/* Run a whole sweep against one QuestionnaireResponse, and report what it
 * wrote so the batch can be taken back.
 *
 * `answerOf` and `writeAnswer` are the mutating instance inside the caller's
 * single `mutateFn`, which is what makes the sequencing right: an item's atoms
 * are applied in the order the record lists them, and each reads the value its
 * predecessor left. The reported `before` is the answer as the pass found it —
 * the whole item, since the sweep may have rewritten several of its lines.
 *
 * Answers carrying no usable value are dropped rather than written: the setter
 * reads an empty value as "delete this answer", so a sweep that passed them
 * through would erase authored text the AI simply had nothing to say about. An
 * answer whose value the item's type cannot take is dropped for a harsher
 * reason — the setter throws on it, and the throw is inside the caller's single
 * `mutateFn`, so passing one through would reject the entire sweep. */
export function applyBulkAccept(
  questionnaireUrl: string,
  answers: readonly SuggestedAnswer[],
  itemOf: (linkId: string) => QuestionnaireItem | undefined,
  answerOf: (linkId: string) => AnswerValue,
  writeAnswer: (linkId: string, value: string | boolean) => void
): BulkAcceptResult {
  const order: string[] = []
  const byLinkId = new Map<string, SuggestedAnswer[]>()
  for (const answer of answers) {
    if (!hasValue(answer, itemOf(answer.linkId))) {
      continue
    }
    const atoms = byLinkId.get(answer.linkId)
    if (atoms) {
      atoms.push(answer)
    } else {
      byLinkId.set(answer.linkId, [answer])
      order.push(answer.linkId)
    }
  }
  const writes: BulkAcceptItemWrite[] = []
  const unplaceableAtomKeys: string[] = []
  for (const linkId of order) {
    const atoms = byLinkId.get(linkId) ?? []
    const item = itemOf(linkId)
    const before = answerOf(linkId)
    let after: string | boolean = typeof before === 'boolean' ? before : ''
    /* Only the atoms this pass actually wrote. An atom the answer has nowhere
     * to put — the atoms listed as couldn't-be-placed rather than shown as
     * cards — is skipped, and settling it anyway would report a goal or
     * intervention as accepted that the sweep never placed. It stays pending
     * until the clinician dismisses it. Judged against the answer the pass
     * found, as the list is; the per-atom checks below still guard the value
     * each write lands on. */
    const held = unplaceableQuestionnaireAtoms(questionnaireUrl, before, atoms)
    const written: SuggestedAnswer[] = []
    for (const atom of atoms) {
      if (held.has(questionnaireAnswerKey(atom))) {
        unplaceableAtomKeys.push(questionnaireAnswerKey(atom))
        continue
      }
      const current = answerOf(linkId)
      const next = questionnaireAtomTargetChanged(
        current,
        atom,
        questionnaireUrl
      )
        ? undefined
        : acceptedAnswerValue(current, atom, undefined, item)
      if (next === undefined) {
        unplaceableAtomKeys.push(questionnaireAnswerKey(atom))
        continue
      }
      after = next
      written.push(atom)
      writeAnswer(linkId, next)
    }
    if (written.length === 0) {
      continue
    }
    writes.push({
      linkId,
      atomKeys: written.map(questionnaireAnswerKey),
      before,
      after,
    })
  }
  return { writes, unplaceableAtomKeys }
}

/* The compensating write that takes one item back to what the sweep found.
 *
 * Compared whole rather than line by line, because the sweep wrote the whole
 * answer in one go: while it still stands exactly as the sweep left it, nothing
 * has been written since and restoring `before` is precise. Once anything in
 * the item has moved, some of the sweep's own lines may have been redrafted and
 * there is no line the reversal could claim untouched, so it REFUSES — the
 * accepted answer stands rather than a clinician's later edit being clobbered
 * by a guess. */
export function planBulkAcceptReversal(
  current: AnswerValue,
  write: BulkAcceptItemWrite
): AnswerReversal {
  if (current === write.after) {
    return { kind: 'write', value: write.before }
  }
  return { kind: 'refuse', reason: ANSWER_CHANGED_REASON }
}
